import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import TagSelector from '../components/TagSelector';
import DataTable from '../components/DataTable';
import IconButton from '../components/IconButton';
import { generateMobileDeLink } from '../utils/mobileDeLink';
import './Cars.css';

function Cars({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [searchParams] = useSearchParams();
  const carRefs = useRef({});
  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [repairs, setRepairs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    customerId: '',
    make: '',
    model: '',
    year: '',
    firstRegistration: '',
    licensePlate: '',
    vin: '',
    mileage: 0,
    hsn: '',
    tsn: '',
    typenschein: '',
    engineCode: '',
    fuelType: '',
    transmission: '',
    image: null
  });
  const [tenant, setTenant] = useState(null);
  const [tags, setTags] = useState([]);

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };

  useEffect(() => {
    if (tenantId) {
      loadTenant();
      loadCars();
      loadCustomers();
      loadTags();
    }
  }, [tenantId]);


  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadCars = async () => {
    try {
      const data = await api.get('/cars', tenantId);
      const customerCars = data.filter(c => c.customerId);
      setCars(customerCars);
      setFilteredCars(customerCars);
    } catch (error) {
      console.error('Error loading cars:', error);
    }
  };

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCars(cars);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = cars.filter(car => {
        const customer = customers.find(c => c.id === car.customerId);
        return (
          (car.make && car.make.toLowerCase().includes(query)) ||
          (car.model && car.model.toLowerCase().includes(query)) ||
          (car.licensePlate && car.licensePlate.toLowerCase().includes(query)) ||
          (car.vin && car.vin.toLowerCase().includes(query)) ||
          (car.hsn && car.hsn.toLowerCase().includes(query)) ||
          (car.tsn && car.tsn.toLowerCase().includes(query)) ||
          (car.typenschein && car.typenschein.toLowerCase().includes(query)) ||
          (car.engineCode && car.engineCode.toLowerCase().includes(query)) ||
          (customer && customer.firstName && customer.firstName.toLowerCase().includes(query)) ||
          (customer && customer.lastName && customer.lastName.toLowerCase().includes(query))
        );
      });
      setFilteredCars(filtered);
    }
  }, [searchQuery, cars, customers]);

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };


  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Ausstehend', class: 'status-pending' },
      in_progress: { label: 'In Bearbeitung', class: 'status-in_progress' },
      completed: { label: 'Abgeschlossen', class: 'status-completed' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'status-pending' };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };


  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? `${customer.firstName} ${customer.lastName}` : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/cars', formData, tenantId);
      setShowForm(false);
      setFormData({
        customerId: '',
        make: '',
        model: '',
        year: '',
        firstRegistration: '',
        licensePlate: '',
        vin: '',
        mileage: 0,
        hsn: '',
        tsn: '',
        typenschein: '',
        engineCode: '',
        image: null,
        accidentVehicle: false
      });
      loadCars();
    } catch (error) {
      console.error('Error saving car:', error);
    }
  };

  const handleEdit = (car) => {
    navigate(`/cars/${car.id}/edit`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Auto wirklich löschen?')) {
      try {
        await api.delete(`/cars/${id}`, tenantId);
        loadCars();
      } catch (error) {
        console.error('Error deleting car:', error);
      }
    }
  };

  return (
    <div className="cars">
      <div className="page-header">
        <h1>Autos</h1>
        <button type="button" onClick={() => setShowForm(true)} className="btn-primary">
          Neues Auto
        </button>
      </div>

      <div style={{ marginBottom: '1.5rem', marginTop: '1.5rem' }}>
        <input
          type="text"
          placeholder="Suchen nach Marke, Modell, Kennzeichen, FIN, HSN, TSN, Kunde..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            maxWidth: '500px',
            padding: '0.75rem 1rem',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            fontSize: '0.9375rem',
            transition: 'all 0.2s ease'
          }}
        />
      </div>

      {showForm && (
        <div className="modal">
          <div className="modal-content">
            <h2>Neues Auto</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Kunde</label>
                <select
                  value={formData.customerId}
                  onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                  required
                >
                  <option value="">Bitte wählen</option>
                  {customers.map(customer => (
                    <option key={customer.id} value={customer.id}>
                      {customer.firstName} {customer.lastName}
                    </option>
                  ))}
                </select>
              </div>
              {tenant?.taxSystem === 'CH' ? (
                <div className="form-group">
                  <label>Typenschein</label>
                  <input
                    type="text"
                    value={formData.typenschein || ''}
                    onChange={(e) => setFormData({ ...formData, typenschein: e.target.value })}
                    placeholder="Typenschein-Nummer"
                  />
                </div>
              ) : (
                <div className="form-row">
                  <div className="form-group">
                    <label>HSN (Herstellerschlüsselnummer)</label>
                    <input
                      type="text"
                      value={formData.hsn || ''}
                      onChange={(e) => setFormData({ ...formData, hsn: e.target.value.toUpperCase() })}
                      placeholder="z.B. 0001"
                      maxLength="4"
                    />
                  </div>
                  <div className="form-group">
                    <label>TSN (Typschlüsselnummer)</label>
                    <input
                      type="text"
                      value={formData.tsn || ''}
                      onChange={(e) => setFormData({ ...formData, tsn: e.target.value.toUpperCase() })}
                      placeholder="z.B. ABC"
                    />
                  </div>
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Marke</label>
                  <input
                    type="text"
                    value={formData.make}
                    onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Modell</label>
                  <input
                    type="text"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Erstzulassung</label>
                  <input
                    type="text"
                    value={formData.firstRegistration}
                    onChange={(e) => setFormData({ ...formData, firstRegistration: e.target.value })}
                    placeholder="MM/YYYY oder YYYY-MM-DD"
                  />
                  <p style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                    Format: MM/YYYY oder volles Datum
                  </p>
                </div>
                <div className="form-group">
                  <label>Baujahr</label>
                  <input
                    type="number"
                    value={formData.year}
                    onChange={(e) => {
                      const val = e.target.value === '' ? '' : e.target.value;
                      setFormData({ ...formData, year: val });
                    }}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                      setFormData({ ...formData, year: val });
                      // Wenn Erstzulassung gesetzt ist, aber kein Jahr, versuche Jahr aus Erstzulassung zu extrahieren
                      if (!val && formData.firstRegistration) {
                        const firstReg = formData.firstRegistration.trim();
                        // Versuche MM/YYYY Format
                        const mmYyyyMatch = firstReg.match(/^(\d{1,2})\/(\d{4})$/);
                        if (mmYyyyMatch) {
                          setFormData(prev => ({ ...prev, year: parseInt(mmYyyyMatch[2]) }));
                        } else {
                          // Versuche YYYY-MM-DD Format
                          const dateMatch = firstReg.match(/^(\d{4})-/);
                          if (dateMatch) {
                            setFormData(prev => ({ ...prev, year: parseInt(dateMatch[1]) }));
                          } else {
                            // Versuche nur YYYY
                            const yearMatch = firstReg.match(/^(\d{4})$/);
                            if (yearMatch) {
                              setFormData(prev => ({ ...prev, year: parseInt(yearMatch[1]) }));
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Kilometerstand</label>
                  <input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: e.target.value === '' ? '' : e.target.value })}
                    onBlur={(e) => {
                      const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                      setFormData({ ...formData, mileage: val });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Auto-Bild</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (!file.type.startsWith('image/')) {
                        showNotification('Bitte wählen Sie eine Bilddatei aus', 'error');
                        return;
                      }
                      if (file.size > 5 * 1024 * 1024) {
                        showNotification('Die Datei ist zu groß. Maximal 5MB erlaubt.', 'error');
                        return;
                      }
                      const reader = new FileReader();
                      reader.onloadend = () => {
                        const img = new Image();
                        img.onload = () => {
                          const canvas = document.createElement('canvas');
                          const maxWidth = 800;
                          const maxHeight = 600;
                          let width = img.width;
                          let height = img.height;
                          if (width > maxWidth || height > maxHeight) {
                            const ratio = Math.min(maxWidth / width, maxHeight / height);
                            width = width * ratio;
                            height = height * ratio;
                          }
                          canvas.width = width;
                          canvas.height = height;
                          const ctx = canvas.getContext('2d');
                          ctx.drawImage(img, 0, 0, width, height);
                          let base64String = canvas.toDataURL('image/jpeg', 0.7);
                          let quality = 0.7;
                          while (base64String.length > 1 * 1024 * 1024 && quality > 0.3) {
                            quality -= 0.1;
                            base64String = canvas.toDataURL('image/jpeg', quality);
                          }
                          if (base64String.length > 1.5 * 1024 * 1024) {
                            showNotification('Bild ist zu groß auch nach Kompression. Bitte wählen Sie ein kleineres Bild.', 'error');
                            return;
                          }
                          setFormData({ ...formData, image: base64String });
                        };
                        img.onerror = () => {
                          showNotification('Fehler beim Laden des Bildes', 'error');
                        };
                        img.src = reader.result;
                      };
                      reader.onerror = () => {
                        showNotification('Fehler beim Lesen der Datei', 'error');
                      };
                      reader.readAsDataURL(file);
                    }}
                  />
                  {formData.image && (
                    <div style={{ marginTop: '0.5rem' }}>
                      <img src={formData.image} alt="Auto Vorschau" style={{ maxWidth: '200px', maxHeight: '150px', border: '1px solid #ddd', borderRadius: '4px' }} />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image: null })}
                        className="btn-small btn-secondary"
                        style={{ marginLeft: '0.5rem' }}
                      >
                        Bild entfernen
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Kennzeichen</label>
                  <input
                    type="text"
                    value={formData.licensePlate}
                    onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>FIN</label>
                  <input
                    type="text"
                    value={formData.vin}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kraftstoffart</label>
                  <select
                    value={formData.fuelType || ''}
                    onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                  >
                    <option value="">Bitte wählen</option>
                    <option value="BENZIN">Benzin</option>
                    <option value="DIESEL">Diesel</option>
                    <option value="ELEKTRO">Elektro</option>
                    <option value="HYBRID">Hybrid</option>
                    <option value="LPG">LPG</option>
                    <option value="CNG">CNG/Erdgas</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Getriebeart</label>
                  <select
                    value={formData.transmission || ''}
                    onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                  >
                    <option value="">Bitte wählen</option>
                    <option value="MANUAL">Manuell</option>
                    <option value="AUTOMATIC">Automatik</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Motorkennbuchstabe</label>
                <input
                  type="text"
                  value={formData.engineCode || ''}
                  onChange={(e) => setFormData({ ...formData, engineCode: e.target.value.toUpperCase() })}
                  placeholder="z.B. ANA"
                  maxLength="4"
                />
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.accidentVehicle || false}
                    onChange={(e) => setFormData({ ...formData, accidentVehicle: e.target.checked })}
                  />
                  <span>Unfallfahrzeug</span>
                </label>
              </div>
              <div className="form-group">
                <label>Tags (optional)</label>
                <TagSelector
                  tags={tags}
                  selectedTags={formData.tags || []}
                  onChange={(selected) => setFormData({ ...formData, tags: selected })}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Erstellen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                  }}
                  className="btn-secondary"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DataTable
        data={filteredCars}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '120px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'make',
            header: 'Marke',
            sortType: 'string',
            accessor: (row) => row.make || '',
            render: (row) => {
              const mainContent = `${row.make || ''} ${row.model || ''}`.trim();
              const mobileDeUrl = generateMobileDeLink(row);
              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div>
                    <button 
                      className="link-button" 
                      onClick={() => navigate(`/cars/${row.id}`)}
                      style={{ fontSize: '1em', fontWeight: 'bold', textAlign: 'left', padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#2196f3' }}
                    >
                      {mainContent}
                    </button>
                  </div>
                  {row.tags && row.tags.length > 0 && (
                    <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                      {row.tags.map(tagId => {
                        const tag = tags.find(t => t.id === tagId);
                        if (!tag) return null;
                        return (
                          <span
                            key={tagId}
                            style={{
                              fontSize: '0.7rem',
                              padding: '0.15rem 0.4rem',
                              borderRadius: '20px',
                              backgroundColor: tag.color,
                              color: '#000',
                              border: `1.5px solid ${tag.color}`,
                              fontWeight: 600
                            }}
                          >
                            {tag.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {mobileDeUrl && (
                    <div style={{ marginTop: '0.25rem' }}>
                      <a
                        href={mobileDeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          fontSize: '0.75rem',
                          color: '#2196f3',
                          textDecoration: 'none'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Auf mobile.de suchen →
                      </a>
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            key: 'year',
            header: 'Baujahr',
            sortType: 'number',
            accessor: (row) => row.year || 0
          },
          {
            key: 'licensePlate',
            header: 'Kennzeichen',
            sortType: 'string',
            accessor: (row) => row.licensePlate || ''
          },
          {
            key: 'mileage',
            header: 'Kilometerstand',
            sortType: 'number',
            align: 'right',
            accessor: (row) => row.mileage || 0,
            render: (row) => `${(row.mileage || 0).toLocaleString('de-DE')} km`
          },
          {
            key: 'customerId',
            header: 'Kunde',
            sortType: 'string',
            accessor: (row) => getCustomerName(row.customerId),
            render: (row) => {
              const customerName = getCustomerName(row.customerId);
              if (!customerName) return '-';
              return (
                <button 
                  className="link-button" 
                  onClick={(e) => {
                    e.stopPropagation();
                    const customer = customers.find(c => c.id === row.customerId);
                    if (customer) {
                      navigate(`/customers/${customer.id}`);
                    }
                  }}
                  style={{ padding: 0, border: 'none', background: 'none', cursor: 'pointer', color: '#2196f3', textDecoration: 'underline' }}
                >
                  {customerName}
                </button>
              );
            }
          },
        ]}
        defaultSort={{ key: 'make', direction: 'asc' }}
        emptyMessage="Keine Fahrzeuge vorhanden"
      />
    </div>
  );
}

export default Cars;

